import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  FileText, Plus, Download, Edit2, Trash2, Calendar, Clock, CheckCircle, 
  AlertTriangle, FileCheck, ChevronRight, Building2, Scale, Briefcase
} from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";

const CATEGORY_CONFIG = {
  monthly: { label: "Monthly", color: "bg-blue-500/20 text-blue-400", icon: Calendar },
  quarterly: { label: "Quarterly", color: "bg-purple-500/20 text-purple-400", icon: Clock },
  annual: { label: "Annual", color: "bg-green-500/20 text-green-400", icon: FileCheck },
  event: { label: "Event-Triggered", color: "bg-orange-500/20 text-orange-400", icon: AlertTriangle }
};

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-gray-500/20 text-gray-400" },
  completed: { label: "Completed", color: "bg-green-500/20 text-green-400" },
  archived: { label: "Archived", color: "bg-slate-500/20 text-slate-400" },
  pending: { label: "Pending", color: "bg-yellow-500/20 text-yellow-400" }
};

export default function GovernancePage({ companyId, company }) {
  const [templates, setTemplates] = useState({});
  const [documents, setDocuments] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("templates");
  
  // Template selection
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  
  // Wizard form state
  const [wizardStep, setWizardStep] = useState(1);
  const [docTitle, setDocTitle] = useState("");
  const [fieldValues, setFieldValues] = useState({});
  const [includeDisclaimer, setIncludeDisclaimer] = useState(false);
  const [customDisclaimer, setCustomDisclaimer] = useState("");
  const [saving, setSaving] = useState(false);
  
  // Edit mode
  const [editingDoc, setEditingDoc] = useState(null);
  
  // Filter
  const [filterCategory, setFilterCategory] = useState("");
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [templatesRes, documentsRes, scheduleRes] = await Promise.all([
        api.get("/governance/templates"),
        api.get(`/companies/${companyId}/governance/documents`),
        api.get(`/companies/${companyId}/governance/schedule?year=${filterYear}`)
      ]);
      
      setTemplates(templatesRes.data.categories || {});
      setDocuments(documentsRes.data.documents || []);
      setStats(documentsRes.data.stats || {});
      setSchedule(scheduleRes.data.schedule || []);
    } catch (err) {
      toast.error("Failed to load governance data");
    } finally {
      setLoading(false);
    }
  }, [companyId, filterYear]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openWizard = async (templateId) => {
    try {
      const res = await api.get(`/governance/templates/${templateId}`);
      setSelectedTemplate(res.data);
      setDocTitle(res.data.name);
      
      // Initialize field values with defaults
      const defaults = {};
      res.data.fields?.forEach(field => {
        defaults[field.name] = field.default_value || "";
      });
      // Pre-fill company name if there's a company field
      if (company?.name) {
        defaults["company_name"] = company.name;
      }
      setFieldValues(defaults);
      setWizardStep(1);
      setIncludeDisclaimer(false);
      setCustomDisclaimer("");
      setShowWizard(true);
    } catch (err) {
      toast.error("Failed to load template");
    }
  };

  const openEditDoc = async (doc) => {
    try {
      const res = await api.get(`/companies/${companyId}/governance/documents/${doc.document_id}`);
      setEditingDoc(res.data);
      setSelectedTemplate(res.data.template);
      setDocTitle(res.data.title);
      setFieldValues(res.data.field_values || {});
      setIncludeDisclaimer(res.data.include_disclaimer || false);
      setCustomDisclaimer(res.data.custom_disclaimer || "");
      setWizardStep(1);
      setShowWizard(true);
    } catch (err) {
      toast.error("Failed to load document");
    }
  };

  const handleSaveDocument = async () => {
    if (!docTitle.trim()) {
      toast.error("Document title is required");
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        template_id: selectedTemplate.template_id,
        title: docTitle.trim(),
        field_values: fieldValues,
        include_disclaimer: includeDisclaimer,
        custom_disclaimer: customDisclaimer.trim()
      };
      
      if (editingDoc) {
        await api.put(`/companies/${companyId}/governance/documents/${editingDoc.document_id}`, data);
        toast.success("Document updated");
      } else {
        await api.post(`/companies/${companyId}/governance/documents`, data);
        toast.success("Document created");
      }
      
      setShowWizard(false);
      setEditingDoc(null);
      setSelectedTemplate(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save document");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (doc) => {
    try {
      const token = localStorage.getItem("auth_token");
      const response = await fetch(
        `${BACKEND_URL}/api/companies/${companyId}/governance/documents/${doc.document_id}/pdf`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: "include" }
      );
      
      if (!response.ok) throw new Error("Download failed");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc.title.replace(/[^a-zA-Z0-9]/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error("Failed to download PDF");
    }
  };

  const handleDeleteDoc = async (doc) => {
    if (!confirm(`Delete "${doc.title}"?`)) return;
    
    try {
      await api.delete(`/companies/${companyId}/governance/documents/${doc.document_id}`);
      toast.success("Document deleted");
      loadData();
    } catch (err) {
      toast.error("Failed to delete document");
    }
  };

  const handleMarkComplete = async (doc) => {
    try {
      await api.put(`/companies/${companyId}/governance/documents/${doc.document_id}`, {
        status: "completed"
      });
      toast.success("Document marked as completed");
      loadData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const updateFieldValue = (name, value) => {
    setFieldValues(prev => ({ ...prev, [name]: value }));
  };

  const renderField = (field) => {
    const value = fieldValues[field.name] || "";
    
    switch (field.field_type) {
      case "select":
        return (
          <Select value={value} onValueChange={(v) => updateFieldValue(field.name, v)}>
            <SelectTrigger className="bg-secondary border-border text-white">
              <SelectValue placeholder={field.placeholder || "Select..."} />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              {field.options?.map(opt => (
                <SelectItem key={opt} value={opt} className="text-white">{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      
      case "textarea":
        return (
          <Textarea
            value={value}
            onChange={(e) => updateFieldValue(field.name, e.target.value)}
            placeholder={field.placeholder || ""}
            className="bg-secondary border-border text-white min-h-[80px]"
            rows={3}
          />
        );
      
      case "date":
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => updateFieldValue(field.name, e.target.value)}
            className="bg-secondary border-border text-white"
          />
        );
      
      case "number":
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => updateFieldValue(field.name, e.target.value)}
            placeholder={field.placeholder || ""}
            className="bg-secondary border-border text-white"
          />
        );
      
      case "currency":
        return (
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-[#8b9299]">$</span>
            <Input
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => updateFieldValue(field.name, e.target.value)}
              placeholder="0.00"
              className="bg-secondary border-border text-white pl-7"
            />
          </div>
        );
      
      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => updateFieldValue(field.name, e.target.value)}
            placeholder={field.placeholder || ""}
            className="bg-secondary border-border text-white"
          />
        );
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  if (!companyId) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Select a company to view governance documents
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="governance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#d4c4b0] flex items-center gap-2">
            <Scale className="w-6 h-6 text-[#e85d24]" />
            Governance & Compliance
          </h1>
          <p className="text-[#8b9299] text-sm mt-1">Corporate governance documents and compliance calendar</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Total Documents</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{stats.total || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Completed</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{stats.completed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-500/20 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Drafts</p>
                <p className="text-xl font-bold text-[#d4c4b0]">{stats.draft || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border card-glow">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-[#8b9299]">Templates</p>
                <p className="text-xl font-bold text-[#d4c4b0]">18</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-secondary mb-4">
          <TabsTrigger value="templates" className="data-[state=active]:bg-[#e85d24]">
            <FileText className="w-4 h-4 mr-2" /> Templates
          </TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-[#e85d24]">
            <FileCheck className="w-4 h-4 mr-2" /> My Documents
          </TabsTrigger>
          <TabsTrigger value="calendar" className="data-[state=active]:bg-[#e85d24]">
            <Calendar className="w-4 h-4 mr-2" /> Calendar
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-6 h-6 border-2 border-[#e85d24] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            Object.entries(templates).map(([catKey, category]) => (
              <Card key={catKey} className="bg-card border-border">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    {CATEGORY_CONFIG[catKey]?.icon && (
                      <div className={`w-10 h-10 rounded-lg ${CATEGORY_CONFIG[catKey]?.color.split(" ")[0]} flex items-center justify-center`}>
                        {(() => {
                          const IconComp = CATEGORY_CONFIG[catKey].icon;
                          return <IconComp className={`w-5 h-5 ${CATEGORY_CONFIG[catKey]?.color.split(" ")[1]}`} />;
                        })()}
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-base font-heading text-[#d4c4b0]">{category.label}</CardTitle>
                      <CardDescription className="text-[#8b9299] text-xs">{category.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {category.templates?.map(template => (
                      <div 
                        key={template.template_id} 
                        className="p-3 bg-secondary/50 rounded-lg border border-border hover:border-[#e85d24]/50 transition-colors cursor-pointer group"
                        onClick={() => openWizard(template.template_id)}
                        data-testid={`template-${template.template_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-[#d4c4b0] group-hover:text-[#e85d24] transition-colors truncate">
                              {template.name}
                            </h4>
                            <p className="text-xs text-[#6b7280] mt-1 line-clamp-2">{template.description}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {template.entity_types?.slice(0, 3).map(et => (
                                <Badge key={et} variant="outline" className="text-[10px] px-1.5 py-0 border-border text-[#8b9299]">
                                  {et}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#6b7280] group-hover:text-[#e85d24] transition-colors flex-shrink-0 ml-2" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <div className="flex items-center gap-2">
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-40 bg-secondary border-border text-white h-9">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all" className="text-white">All Categories</SelectItem>
                {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => (
                  <SelectItem key={key} value={key} className="text-white">{cfg.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              {documents.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-[#6b7280]">
                  <FileText className="w-12 h-12 mb-3 opacity-50" />
                  <p>No governance documents created yet</p>
                  <Button variant="link" onClick={() => setActiveTab("templates")} className="text-[#e85d24] mt-2">
                    Browse templates to get started
                  </Button>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {documents
                    .filter(d => !filterCategory || filterCategory === "all" || d.category === filterCategory)
                    .map(doc => (
                    <div key={doc.document_id} className="p-4 hover:bg-secondary/30 transition-colors" data-testid={`doc-${doc.document_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-medium text-[#d4c4b0] truncate">{doc.title}</h4>
                            <Badge className={STATUS_CONFIG[doc.status]?.color || STATUS_CONFIG.draft.color}>
                              {STATUS_CONFIG[doc.status]?.label || doc.status}
                            </Badge>
                            <Badge className={CATEGORY_CONFIG[doc.category]?.color || "bg-gray-500/20 text-gray-400"}>
                              {CATEGORY_CONFIG[doc.category]?.label || doc.category}
                            </Badge>
                          </div>
                          <p className="text-xs text-[#6b7280] mt-1">{doc.template_name}</p>
                          <p className="text-xs text-[#6b7280]">
                            Created: {new Date(doc.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]" onClick={() => handleDownloadPDF(doc)} title="Download PDF">
                            <Download className="w-4 h-4" />
                          </Button>
                          {doc.status === "draft" && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8b9299] hover:text-[#d4c4b0]" onClick={() => openEditDoc(doc)} title="Edit">
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-green-400 hover:text-green-300" onClick={() => handleMarkComplete(doc)} title="Mark Complete">
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteDoc(doc)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          <div className="flex items-center gap-2">
            <Label className="text-[#8b9299]">Year:</Label>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-24 bg-secondary border-border text-white h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()} className="text-white">{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-base text-[#d4c4b0]">Governance Calendar {filterYear}</CardTitle>
              <CardDescription className="text-[#8b9299]">
                Schedule and track your governance document deadlines
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-[#6b7280]">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Calendar scheduling coming soon</p>
                <p className="text-xs mt-1">Create documents from templates to track your compliance</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Document Wizard Dialog */}
      <Dialog open={showWizard} onOpenChange={(open) => { if (!open) { setShowWizard(false); setEditingDoc(null); } }}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="governance-wizard">
          <DialogHeader>
            <DialogTitle className="font-heading text-white flex items-center gap-2">
              <Scale className="w-5 h-5 text-[#e85d24]" />
              {editingDoc ? "Edit Document" : "Create Governance Document"}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="space-y-4 py-2">
              {/* Template info */}
              <div className="p-3 bg-secondary/50 rounded-lg border border-border">
                <h4 className="text-sm font-medium text-[#d4c4b0]">{selectedTemplate.name}</h4>
                <p className="text-xs text-[#6b7280] mt-1">{selectedTemplate.description}</p>
              </div>
              
              {/* Document Title */}
              <div>
                <Label className="text-[#8b9299] text-sm">Document Title</Label>
                <Input 
                  value={docTitle} 
                  onChange={(e) => setDocTitle(e.target.value)} 
                  placeholder="Enter document title..."
                  className="mt-1.5 bg-secondary border-border text-white"
                  data-testid="doc-title-input"
                />
              </div>
              
              <Separator className="bg-border/50" />
              
              {/* Dynamic Fields */}
              <div className="space-y-4">
                {selectedTemplate.fields?.map((field, idx) => (
                  <div key={field.name}>
                    <Label className="text-[#8b9299] text-sm">
                      {field.label}
                      {field.required && <span className="text-red-400 ml-1">*</span>}
                    </Label>
                    <div className="mt-1.5">
                      {renderField(field)}
                    </div>
                  </div>
                ))}
              </div>
              
              <Separator className="bg-border/50" />
              
              {/* Disclaimer Toggle */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="disclaimer" 
                    checked={includeDisclaimer} 
                    onCheckedChange={setIncludeDisclaimer}
                    className="border-border"
                  />
                  <Label htmlFor="disclaimer" className="text-[#8b9299] text-sm cursor-pointer">
                    Include legal disclaimer on document
                  </Label>
                </div>
                
                {includeDisclaimer && (
                  <div>
                    <Label className="text-[#8b9299] text-sm">Custom Disclaimer (optional)</Label>
                    <Textarea
                      value={customDisclaimer}
                      onChange={(e) => setCustomDisclaimer(e.target.value)}
                      placeholder="Leave blank for default disclaimer: 'This document was prepared for internal corporate governance purposes...'"
                      className="mt-1.5 bg-secondary border-border text-white"
                      rows={3}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setShowWizard(false); setEditingDoc(null); }} className="border-border text-white">
              Cancel
            </Button>
            <Button onClick={handleSaveDocument} disabled={saving || !docTitle.trim()} className="btn-primary-gradient" data-testid="save-gov-doc-btn">
              {saving ? "Saving..." : (editingDoc ? "Update Document" : "Create Document")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
